// P2-A JWT Hardening — client-side auth store with silent refresh.
//
// Token storage strategy (auto-detected from login response):
//   - LEGACY (csrf_token absent from /auth/login response):
//       Access token persists in localStorage["token"] — original behaviour.
//       Survives reload by itself.
//   - HARDENED (csrf_token present):
//       Access token kept in JS memory only.
//       Refresh + CSRF cookies set by backend.
//       On page reload: app boots, sees no in-memory access token, calls
//       /api/auth/refresh once (the refresh cookie + readable csrf cookie
//       are sent automatically). If that succeeds → restore session silently.

import { readCookie } from "@/lib/cookies";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LEGACY_KEY = "token";
const CSRF_COOKIE = "qv_csrf";

let _accessToken = null;
let _csrfToken = null;
let _mode = null; // "legacy" | "hardened" — locked on first successful login
let _refreshInFlight = null; // single-flight promise

const subscribers = new Set();

function notify() {
  for (const cb of subscribers) {
    try { cb(_accessToken); } catch (_) { /* noop */ }
  }
}

export const authStore = {
  subscribe(cb) {
    subscribers.add(cb);
    return () => subscribers.delete(cb);
  },
  getAccessToken() {
    if (_accessToken) return _accessToken;
    // Legacy mode: hydrate from localStorage on first read
    if (_mode !== "hardened") {
      try {
        const t = localStorage.getItem(LEGACY_KEY);
        if (t) {
          _accessToken = t;
          _mode = "legacy";
          return t;
        }
      } catch (_) { /* private mode */ }
    }
    return null;
  },
  getCsrfToken() {
    if (_csrfToken) return _csrfToken;
    // Fallback to cookie (set by backend; readable JS-side)
    return readCookie(CSRF_COOKIE);
  },
  setSession({ access_token, csrf_token }) {
    _accessToken = access_token || null;
    if (csrf_token) {
      _mode = "hardened";
      _csrfToken = csrf_token;
      // Best-effort clear legacy storage so we don't leave a stale long-lived token
      try { localStorage.removeItem(LEGACY_KEY); } catch (_) { /* noop */ }
    } else {
      _mode = "legacy";
      _csrfToken = null;
      try {
        if (access_token) localStorage.setItem(LEGACY_KEY, access_token);
      } catch (_) { /* noop */ }
    }
    notify();
  },
  clear() {
    _accessToken = null;
    _csrfToken = null;
    try { localStorage.removeItem(LEGACY_KEY); } catch (_) { /* noop */ }
    notify();
  },
  isHardened() {
    return _mode === "hardened";
  },
  /**
   * Silent refresh. Single-flight: concurrent callers share one in-flight
   * /auth/refresh promise so we never burn the rotation slot twice.
   * Returns true on success, false otherwise.
   */
  async refreshSilently() {
    if (_refreshInFlight) return _refreshInFlight;
    _refreshInFlight = (async () => {
      const csrf = this.getCsrfToken();
      if (!csrf) return false;
      try {
        const res = await fetch(`${API_URL}/auth/refresh`, {
          method: "POST",
          headers: { "X-CSRF-Token": csrf, "Content-Type": "application/json" },
        });
        if (!res.ok) return false;
        const body = await res.json();
        this.setSession({
          access_token: body.access_token,
          csrf_token: body.csrf_token,
        });
        return true;
      } catch (_) {
        return false;
      } finally {
        // small microtask yield so concurrent callers actually share the
        // same in-flight promise
        setTimeout(() => { _refreshInFlight = null; }, 0);
      }
    })();
    return _refreshInFlight;
  },
};

export default authStore;
